/**
 * Row shapes shared by the field app's local SQLite mirror, the sync engine and the
 * web dashboard. They follow supabase/migrations/*.sql column for column.
 *
 * Geometry: PostgREST returns PostGIS columns as hex EWKB, which no client wants.
 * Migration 0002 therefore adds generated `lat` / `lng` (and `*_geojson`) columns
 * that every SELECT uses, and clients WRITE geometry as EWKT via `pointEwkt()`.
 * The raw `geom` / `boundary` columns are never read on a client.
 */
import type {
  Condition,
  HeightMethod,
  InterventionType,
  InterventionUnit,
  MediaKind,
  Role,
  SoilMoisture,
  SoilTemp,
  SpeciesType,
  TreeStatus,
} from './enums';

/** ISO-8601 timestamp string (timestamptz) or date string (date). */
export type IsoTimestamp = string;
export type IsoDate = string;
export type Uuid = string;

export interface GeoJsonPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}
export interface GeoJsonPoint {
  type: 'Point';
  coordinates: [number, number];
}

export interface ProfileRow {
  user_id: Uuid;
  display_name: string;
  is_admin: boolean;
  created_at: IsoTimestamp;
}

export interface SpeciesRow {
  id: Uuid;
  name_es: string;
  name_en: string;
  scientific: string | null;
  type: SpeciesType;
  native: boolean | null;
  notes: string | null;
  created_at: IsoTimestamp;
  updated_at: IsoTimestamp;
}

export interface ProjectRow {
  id: Uuid;
  code: string;
  name: string;
  site: string | null;
  boundary_geojson: GeoJsonPolygon | null;
  planted_on: IsoDate | null;
  lead_user: Uuid | null;
  /** Storage path of the offline basemap pack, e.g. `basemap/DS.pmtiles`; null until T1.05 builds one. */
  basemap_path: string | null;
  created_at: IsoTimestamp;
  updated_at: IsoTimestamp;
}

export interface ProjectMemberRow {
  project_id: Uuid;
  user_id: Uuid;
  role: Role;
  created_at: IsoTimestamp;
}

export interface TeamRow {
  id: Uuid;
  project_id: Uuid;
  name: string;
  /** Never leaves the server; the client only ever sees the name. */
  code_hash?: string;
  active: boolean;
  created_at: IsoTimestamp;
}

export interface ZoneRow {
  id: Uuid;
  project_id: Uuid;
  label: string;
  geom_geojson: GeoJsonPolygon | null;
  centroid_lat: number | null;
  centroid_lng: number | null;
  grid_rows: number | null;
  grid_cols: number | null;
  created_at: IsoTimestamp;
  updated_at: IsoTimestamp;
}

export interface TreeRow {
  id: Uuid;
  client_id: Uuid;
  project_id: Uuid;
  zone_id: Uuid | null;
  tag: string;
  species_id: Uuid | null;
  lat: number | null;
  lng: number | null;
  gps_accuracy_m: number | null;
  grid_row: number | null;
  grid_col: number | null;
  planted_on: IsoDate | null;
  status: TreeStatus;
  replaced_by: Uuid | null;
  created_by: Uuid | null;
  created_at: IsoTimestamp;
  updated_at: IsoTimestamp;
}

export interface SurveyRow {
  id: Uuid;
  client_id: Uuid;
  project_id: Uuid;
  zone_id: Uuid | null;
  date: IsoDate;
  team_id: Uuid | null;
  team_name: string | null;
  /** Members' initials only, e.g. "JA, MR, LS". Never full names. */
  members: string | null;
  weather: string[];
  care_plan: string | null;
  mulch_plan: string | null;
  pest_notes: string | null;
  irrigation_bottles: number | null;
  notes: string | null;
  created_by: Uuid | null;
  device_time: IsoTimestamp | null;
  created_at: IsoTimestamp;
  updated_at: IsoTimestamp;
}

export interface MeasurementRow {
  id: Uuid;
  client_id: Uuid;
  tree_id: Uuid;
  survey_id: Uuid;
  condition: Condition;
  height_cm: number | null;
  height_method: HeightMethod | null;
  soil_moisture: SoilMoisture | null;
  soil_temp: SoilTemp | null;
  chips: string[];
  note: string | null;
  device_lat: number | null;
  device_lng: number | null;
  device_accuracy_m: number | null;
  recorded_by: Uuid | null;
  team_id: Uuid | null;
  device_time: IsoTimestamp | null;
  recorded_at: IsoTimestamp;
}

export interface GirthRow {
  measurement_id: Uuid;
  height_cm: 10 | 130;
  girth_cm: number;
  /** Generated on the server (girth / pi); clients compute it with diameterFromGirthCm for display. */
  diameter_cm?: number;
}

export interface InterventionRow {
  id: Uuid;
  client_id: Uuid;
  project_id: Uuid;
  tree_id: Uuid | null;
  zone_id: Uuid | null;
  survey_id: Uuid | null;
  type: InterventionType;
  quantity: number | null;
  unit: InterventionUnit | null;
  note: string | null;
  done_by: Uuid | null;
  team_id: Uuid | null;
  done_at: IsoTimestamp;
  recorded_at: IsoTimestamp;
}

export interface MediaRow {
  id: Uuid;
  client_id: Uuid;
  project_id: Uuid;
  tree_id: Uuid | null;
  survey_id: Uuid | null;
  measurement_id: Uuid | null;
  /** `<project_id>/<tree_id or survey_id>/<media_id>.jpg` in the `media` bucket. */
  storage_path: string | null;
  kind: MediaKind;
  taken_at: IsoTimestamp | null;
  lat: number | null;
  lng: number | null;
  caption: string | null;
  uploaded_by: Uuid | null;
  team_id: Uuid | null;
  uploaded_at: IsoTimestamp;
  upload_complete: boolean;
}

/** The `tree_latest` view. */
export interface TreeLatestRow {
  tree_id: Uuid;
  project_id: Uuid;
  zone_id: Uuid | null;
  tag: string;
  species_id: Uuid | null;
  status: TreeStatus;
  measurement_id: Uuid | null;
  survey_date: IsoDate | null;
  condition: Condition | null;
  height_cm: number | null;
  girth_basal_cm: number | null;
  diameter_basal_cm: number | null;
  girth_dbh_cm: number | null;
  diameter_dbh_cm: number | null;
  recorded_at: IsoTimestamp | null;
}

/** The `survey_summary` view: the worksheet's section 2. */
export interface SurveySummaryRow {
  survey_id: Uuid;
  project_id: Uuid;
  zone_id: Uuid | null;
  date: IsoDate;
  team_name: string | null;
  found: number;
  alive: number;
  not_found: number;
  survival_rate_pct: number | null;
}

export interface ZoneSurvivalRow extends SurveySummaryRow {
  zone_label: string | null;
  planted: number;
  cumulative_survival_pct: number | null;
}

export interface TreeFlagsRow {
  tree_id: Uuid;
  stressed_twice: boolean | null;
  not_found_last: boolean | null;
  not_found_twice: boolean | null;
  possible_mis_tag: boolean | null;
}

/** Claims the team-code sign-in puts in the session's app_metadata (migration 0002, claim_team). */
export interface TeamClaims {
  team_id: Uuid;
  project_id: Uuid;
  team_name: string;
  /** ISO timestamp after which the claim no longer grants the field role. */
  team_expires_at: IsoTimestamp;
}

/** EWKT for a WGS84 point, the form PostgREST accepts for a geometry column. */
export function pointEwkt(lat: number, lng: number): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new RangeError('lat/lng must be finite');
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) throw new RangeError(`lat/lng out of range: ${lat}, ${lng}`);
  return `SRID=4326;POINT(${lng} ${lat})`;
}

/** EWKT for a WGS84 polygon from GeoJSON (first ring outer, rest holes). */
export function polygonEwkt(polygon: GeoJsonPolygon): string {
  const rings = polygon.coordinates.map((ring) => `(${ring.map(([x, y]) => `${x} ${y}`).join(', ')})`).join(', ');
  return `SRID=4326;POLYGON(${rings})`;
}
